import { useState } from 'react';

import { useBulkCreateRights, useRights } from '../../api/hooks';
import { Badge, Button, useToast } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';
import { draftToInput, EMPTY_RIGHT_DRAFT, RightForm } from '../rights/RightForm';
import type { RightDraft } from '../rights/RightForm';

interface RightsStepSectionProps {
  showId: string;
  seasonId: string | null;
}

const newDraft = (showId: string, seasonId: string | null): RightDraft => ({
  ...EMPTY_RIGHT_DRAFT,
  showId,
  seasonId: seasonId ?? '',
});

/** Embeds the rights module inside the wizard, pre-scoped to the detected show/season. */
export function RightsStepSection({ showId, seasonId }: RightsStepSectionProps) {
  const { showToast } = useToast();
  const rights = useRights({ showId, limit: 100 });
  const bulkCreateRights = useBulkCreateRights();

  const [drafts, setDrafts] = useState<RightDraft[]>([newDraft(showId, seasonId)]);
  const [benchmarkOpen, setBenchmarkOpen] = useState(false);

  const rightsForSeason = (rights.data?.data ?? []).filter(
    (right) => seasonId === null || right.seasonId === seasonId || right.seasonId === null,
  );
  const hasRights = rightsForSeason.length > 0;

  function updateDraft(index: number, next: RightDraft) {
    setDrafts((current) =>
      current.map((entry, entryIndex) => (entryIndex === index ? next : entry)),
    );
  }

  function duplicateDraft(index: number) {
    setDrafts((current) => {
      const source = current[index];
      return source === undefined ? current : [...current, { ...source }];
    });
  }

  function removeDraft(index: number) {
    setDrafts((current) => (current.length <= 1 ? current : current.filter((_, i) => i !== index)));
  }

  async function handleSubmit() {
    const inputs = drafts.map(draftToInput);

    if (inputs.some((input) => input === null)) {
      showToast('Every entry needs at least one media type and a territory', 'error');
      return;
    }

    try {
      const result = await bulkCreateRights.mutateAsync(
        inputs.filter((input): input is NonNullable<typeof input> => input !== null),
      );
      showToast(`${result.count} right(s) added`);
      setDrafts([newDraft(showId, seasonId)]);
    } catch (error) {
      showToast(errorMessage(error, 'Could not add rights'), 'error');
    }
  }

  return (
    <section className="rfp-card">
      <div className="rfp-card__head">
        <div>
          <h2 className="rfp-card__title">Rights</h2>
          <p className="rfp-card__caption">
            Add rights granted in this contract. On approval, these are automatically saved to the
            Rights Management system.
          </p>
        </div>
      </div>

      <Button onClick={() => setBenchmarkOpen((open) => !open)} type="button" variant="secondary">
        {benchmarkOpen ? '▾' : '▸'} Benchmark from prior deals
      </Button>
      {benchmarkOpen && (
        <p className="rfp-muted-text" style={{ marginTop: 8 }}>
          Coming soon — compare this contract's rights against prior deals for the same show.
        </p>
      )}

      {!hasRights && (
        <p className="rfp-notice" role="status" style={{ marginTop: 16 }}>
          No rights added yet. If you approve now, nothing will be saved to the Rights Management
          system for this contract. Add at least one right (party, territory, media type, etc.)
          before approving.
        </p>
      )}

      {rights.isLoading ? (
        <p className="rfp-loading">Loading rights…</p>
      ) : (
        <ul className="rfp-stack" style={{ marginTop: hasRights ? 16 : 0 }}>
          {rightsForSeason.map((right) => (
            <li className="rfp-mono-text" key={right.id}>
              {right.mediaType} · {right.territory}
              {right.platform ? ` · ${right.platform}` : ''}
            </li>
          ))}
        </ul>
      )}

      <div className="rfp-stack" style={{ marginTop: 16 }}>
        {drafts.map((draft, index) => (
          <div className="rfp-card" key={index}>
            <div className="rfp-card__head">
              <div>
                {draft.isBackendGuaranteed && <Badge tone="teal">+ Backend Guaranteed</Badge>}
              </div>
              <div className="rfp-row-actions">
                <Button onClick={() => duplicateDraft(index)} type="button" variant="secondary">
                  Duplicate
                </Button>
                <Button
                  disabled={drafts.length <= 1}
                  onClick={() => removeDraft(index)}
                  type="button"
                  variant="secondary"
                >
                  Remove
                </Button>
              </div>
            </div>
            <RightForm
              draft={draft}
              idPrefix={`wizard-right-${index}`}
              lockedSeasonId={seasonId ?? undefined}
              lockedShowId={showId}
              onChange={(next) => updateDraft(index, next)}
            />
          </div>
        ))}
      </div>

      <div className="rfp-wizard-actions">
        <Button
          onClick={() => setDrafts((current) => [...current, newDraft(showId, seasonId)])}
          type="button"
          variant="secondary"
        >
          + Add another
        </Button>
        <Button disabled={bulkCreateRights.isPending} onClick={handleSubmit} type="button">
          {bulkCreateRights.isPending ? 'Saving…' : `Save ${drafts.length} right(s)`}
        </Button>
      </div>
    </section>
  );
}
